import React, { useEffect, useState } from "react";
import { Backdrop, Button, LogoLoader, Modal, showToast, StatusTags, TableAdv } from "../../../../../../components";
import { mapTableRows } from "../../../../../../utils";
import { fetchAllAiCallsApi, updateAiCallApi } from "../../../../../../redux/slices/aicalls/aicallsApi";
import { convertFromUTC, extractDateAndTimeFromFormatted } from "../../../../../../utils/dateUtils";
import { totalCountStart } from "../../../../../../redux/slices/totalCount/totalCountSlice";
import { useDispatch, useSelector } from "react-redux";
import { usePagination } from "../../../../../../hooks";

const formatDateTime = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  const optionsDate = { year: "numeric", month: "long", day: "2-digit" };
  const formattedDate = date.toLocaleDateString("en-US", optionsDate);

  const optionsTime = {
    hour: "2-digit",
    minute: "2-digit",
    // second: "2-digit",
    hour12: true,
  };
  const formattedTime = date.toLocaleTimeString("en-US", optionsTime);

  return `${formattedDate} ${formattedTime}`;
};

const CallScheduleList = ({ jobPostId }) => {
  const [loading, setLoading] = useState(false);
  const [aiCalls, setAiCalls] = useState([]);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelCallId, setCancelCallId] = useState();

  const dispatch = useDispatch();
  const { page, limit, setPage, handleLimitChange } = usePagination(10, (newPage, newLimit) => {});
  const { value } = useSelector((state) => state.totalCount);
  const totalCount = value?.jobpostaiclls?.data || 0

  const aiCallStatusMap = {
    1: "Initiated",
    2: "Success",
    3: "Failed",
    4: "Skipped",
    5: "Scheduled"
  }

  const columns = [
    {
      key: "candidateName",
      label: "Candidate Name",
      columnMenu: false,
    },
    {
      key: "role",
      label: "Role",
      columnMenu: false,
    },
    {
      key: "addedBy",
      label: "Added By",
      columnMenu: false,
      render: (row) => row?.userType === 2 ? (
        <div className="flex items-center gap-1">
          <Button type="icon" color="danger" variant="text" icon="android" className="!cursor-default"/>
          <span className="text-sm">{row.addedBy}</span>
        </div>
      ) : (<span className="text-sm">{row.addedBy}</span>)
    },
    {
      key: "addedOn",
      label: "Added On",
      columnMenu: false,
    },
    {
      key: "status",
      label: "Status",
      columnMenu: false,
      render: (row) => <StatusTags tag={row.status} />,
    },
    {
      key: "callType",
      label: "Call Type"
    },
    {
      key: "actions",
      label: "Actions",
      columnMenu: false,
      render: (row) =>  (
        <Button
          size="sm"
          title={"Cancel"}
          color="danger"
          disabled={row.status !== "Initiated"}
          onClick={() => {
            setIsCancelModalOpen(true);
            setCancelCallId(row.uiid);
          }}
        />
      )
    },
  ];

  const rows = aiCalls.map((call) => {
    const localDateTime = convertFromUTC(call?.callinitiatedTime);
    const { date, time } = extractDateAndTimeFromFormatted(localDateTime);

    return {
      candidateName: `${call?.candidateId?.firstName || ""} ${call?.candidateId?.lastName || ""}`,
      role: call?.jobPostId?.jobId?.title,
      date: date || "-",
      time: time || "-",
      status: aiCallStatusMap[call?.status],
      uiid: call?.uiid,
      addedBy: `${call?.addedBy?.firstName} ${call?.addedBy?.lastName}`,
      userType: call?.addedBy?.userType || 1,
      addedOn: formatDateTime(convertFromUTC(call?.addedOn)),
      callType:  call?.callType === 2 ? "Incoming" : "Outgoing",
    };
  });

  const handleCancelCall = async ({ uiid }) => {
    if (!uiid) return;
    try {
      const payload = {
        coll: "AiCall",
        fil: { uiid: { type: "NONE", value: uiid } },
        ref: "Candidate JobPost",
        incl: ["uiid firstName lastName", "uiid jobId"],
        extret: "uiid firstName lastName"
      }
      const response = await fetchAllAiCallsApi(payload);

      if (response?.success) {
        const status = response?.data?.data?.[0]?.status;

        if (status === 1) {
          const cancelResponse = await updateAiCallApi({
            coll: "AiCall", 
            uiid,
            payload: [{
              type: "SET", 
              value: {status: 9}
            }]
          });
          if (cancelResponse?.success) {
            showToast("AI Call cancelled successfully", "success");
            fetchCountOfAiCalls();
          }
        }
      }
    } catch (error) {
      console.error("Error cancelling AI Call: ", error);
    }
  }

  const fetchAiCalls = async () => {
    try {
      setLoading(true);
      const payload = {
        coll: "AiCall",
        fil: { 
          jobPostId: { type: "NONE", value: jobPostId },
          status: { type: "IN", value: [1,5] }
        },
        ref: "Candidate JobPost",
        incl: ["uiid firstName lastName", "uiid jobId"],
        extret: "uiid firstName lastName userType"
      }
      const response = await fetchAllAiCallsApi(payload);

      if (response?.success) {
        setAiCalls(response?.data?.data);
      } else if (response?.msg?.includes("You've reached the end")) {
        setAiCalls([]);
      }
    } catch (error) {
      console.error("Error fetching AI Calls:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountOfAiCalls = () => {
    dispatch(
      totalCountStart({
        collection: "aicalls",
        coll: "AiCall",
        filters: { 
          jobPostId: { type: "NONE", value: jobPostId },
          status: { type: "IN", value: [1,5] }
        },
        tname: "jobpostaiclls",
        app: "ats",
      })
    );
  };

  useEffect(() => {
    fetchCountOfAiCalls();
  }, []);

  useEffect(() => {
    fetchAiCalls();
  }, [jobPostId, page, limit, value?.jobpostaiclls]);

  const mappedRows = mapTableRows(rows, columns);
  return (
    <div className="flex flex-col mt-5 overflow-auto">
      <div className="flex flex-grow overflow-auto gap-4 mx-0.5">
        <TableAdv
          showSlNo
          columns={columns}
          rows={mappedRows}
          totalCount={totalCount}
          serverPagination={true}
          page={page}
          limit={limit}
          toolbarRight={{
            fullScreen: true,
          }}
        />
      </div>

      <Modal
        isOpen={isCancelModalOpen}
        onClose={() => {
          setIsCancelModalOpen(false)
          setCancelCallId();
        }}
        title="Confirm Cancellation"
      >
        <p className="text-center">Are you sure you want to cancel this scheduled AI call?</p>
        <div className="flex justify-center gap-3 mt-4">
          <Button
            title="No"
            variant="outlined"
            onClick={() => {
              setIsCancelModalOpen(false);
              setCancelCallId();
            }}
          />
          <Button
            title="Yes"
            onClick={() => {
              handleCancelCall({ uiid: cancelCallId });
              setIsCancelModalOpen(false);
              setCancelCallId();
            }}
          />
        </div>
      </Modal>

      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default CallScheduleList;
